import { useEffect, useRef, useState } from 'react';
import { getAudioContextCtor } from '@/lib/voice/audioContext';
import { useAuthStore } from '@/stores/authStore';
import { useDataStore } from '@/stores/dataStore';
import { api } from '@/lib/api';
import { useVoiceMediaSnapshot, voiceMedia } from '@/lib/voiceMedia';
import { hasVoiceIntent } from '@/lib/voiceIntent';
import { loadRtcConfigWithRetry, RTC_CONFIG_REASK_MS } from '@/lib/voice/rtcConfigRetry';
import { effectiveGain, isLocallyMuted, useVoiceLocalAudio } from '@/lib/voiceLocalAudio';
import { useSpeakingMap } from '@/lib/voiceLevels';
import { getVoicePreferences, subscribeVoicePreferences } from '@/lib/voicePreferences';
import { resolveOutputSink } from '@/lib/voice/voiceMediaHelpers';
import { useVoiceUiStore } from '@/stores/voiceUiStore';
import { toast } from '@/lib/toast';
import i18n from '@/i18n';

const EMPTY_ROOM: import('@/types/api').VoiceUserState[] = [];

export function VoiceAudioLayer() {
  const meId = useAuthStore((s) => s.user?.id ?? null);
  const myVoiceChannelId = useDataStore((s) => s.myVoiceChannelId);
  const audioBitrateKbps = useDataStore((s) => {
    if (!myVoiceChannelId) return 96;
    for (const guild of Object.values(s.guilds)) {
      const channel = guild.channels.find((entry) => entry.id === myVoiceChannelId);
      if (channel) return channel.bitrateKbps ?? 96;
    }
    return 96;
  });
  const room = useDataStore((s) =>
    myVoiceChannelId ? s.voiceByChannel[myVoiceChannelId] ?? EMPTY_ROOM : EMPTY_ROOM,
  );
  const myVoiceState = useDataStore((s) =>
    meId && myVoiceChannelId
      ? s.voiceByChannel[myVoiceChannelId]?.find((user) => user.userId === meId) ?? null
      : null,
  );
  const snapshot = useVoiceMediaSnapshot();

  // Mic transmission gate:
  //   open  → always transmit
  //   ptt   → transmit only while the push-to-talk key is held
  //   voice → transmit only while the local speaking detector is open
  // Manual mute still wins (handled in voiceMedia.applyTransmit).
  const [micMode, setMicMode] = useState(() => getVoicePreferences().micMode);
  const [pttKey, setPttKey] = useState(() => getVoicePreferences().pttKey);
  const [pttHeld, setPttHeld] = useState(false);
  useEffect(
    () =>
      subscribeVoicePreferences(() => {
        const p = getVoicePreferences();
        setMicMode(p.micMode);
        setPttKey(p.pttKey);
      }),
    [],
  );
  // Re-route the shared output context when the user picks a different speaker
  // (Chromium/Electron). No-op until a context exists / on non-Chromium.
  useEffect(() => subscribeVoicePreferences(() => void applyOutputSink()), []);

  const localSpeaking = useSpeakingMap().get('local') ?? false;

  // Push-to-talk listeners (global). Supports a keyboard key (KeyboardEvent
  // .code) or a mouse button ("Mouse1" middle / "Mouse3"|"Mouse4" side).
  useEffect(() => {
    if (micMode !== 'ptt' || !pttKey) {
      setPttHeld(false);
      return;
    }
    if (pttKey.startsWith('Mouse')) {
      const button = Number(pttKey.slice(5));
      const down = (e: MouseEvent) => {
        if (e.button === button) {
          e.preventDefault(); // suppress side-button back/forward navigation
          setPttHeld(true);
        }
      };
      const up = (e: MouseEvent) => {
        if (e.button === button) {
          e.preventDefault();
          setPttHeld(false);
        }
      };
      const aux = (e: MouseEvent) => {
        if (e.button === button) e.preventDefault();
      };
      window.addEventListener('mousedown', down, true);
      window.addEventListener('mouseup', up, true);
      window.addEventListener('auxclick', aux, true);
      return () => {
        window.removeEventListener('mousedown', down, true);
        window.removeEventListener('mouseup', up, true);
        window.removeEventListener('auxclick', aux, true);
      };
    }
    const down = (e: KeyboardEvent) => {
      if (e.code === pttKey && !e.repeat) setPttHeld(true);
    };
    const up = (e: KeyboardEvent) => {
      if (e.code === pttKey) setPttHeld(false);
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, [micMode, pttKey]);

  // Leaving the page/window with the push-to-talk key DOWN never delivers the
  // keyup (the events go to whatever the user switched to), so the gate stayed
  // open and the microphone kept transmitting from a tab they'd walked away
  // from. Release on blur/hide. Also tell them once why PTT is silent there:
  // a browser (and an unfocused Electron window) simply doesn't get the
  // keystroke, so «по кнопке» cannot work in the background at all.
  useEffect(() => {
    if (micMode !== 'ptt') return;
    let hinted = false;
    const release = () => setPttHeld(false);
    const onHidden = () => {
      if (!document.hidden) return;
      release();
      if (hinted || !myVoiceChannelId) return;
      hinted = true;
      toast.info(i18n.t('voice.pttBackgroundHint'), { duration: 9000 });
    };
    window.addEventListener('blur', release);
    document.addEventListener('visibilitychange', onHidden);
    return () => {
      window.removeEventListener('blur', release);
      document.removeEventListener('visibilitychange', onHidden);
    };
  }, [micMode, myVoiceChannelId]);

  useEffect(() => {
    const open =
      micMode === 'open' ? true : micMode === 'ptt' ? pttHeld : localSpeaking;
    voiceMedia.setMicGateOpen(open);
  }, [micMode, pttHeld, localSpeaking]);

  // Voice connects only once /api/rtc/config has answered. Failures are
  // retried with a growing delay; if it never answers, the call says it can't
  // connect audio, and joining a channel after that asks again.
  const [rtcConfigRequest, setRtcConfigRequest] = useState(0);
  useEffect(() => {
    let cancelled = false;
    void loadRtcConfigWithRetry({ load: () => api.rtcConfig(), isCancelled: () => cancelled })
      .then((config) => {
        if (config && !cancelled) voiceMedia.markRtcReady();
      })
      .catch(() => {
        if (!cancelled) voiceMedia.markRtcUnavailable();
      });
    return () => {
      cancelled = true;
    };
  }, [rtcConfigRequest]);
  useEffect(() => {
    if (!myVoiceChannelId) return;
    if (voiceMedia.isRtcUnavailable()) setRtcConfigRequest((n) => n + 1);
    // Still in the channel after a round of retries gave up (e.g. a reload
    // restored the call while the API was down): keep asking, slowly.
    const timer = window.setInterval(() => {
      if (voiceMedia.isRtcUnavailable()) setRtcConfigRequest((n) => n + 1);
    }, RTC_CONFIG_REASK_MS);
    return () => window.clearInterval(timer);
  }, [myVoiceChannelId]);

  useEffect(() => {
    if (!meId || !myVoiceChannelId || !hasVoiceIntent(myVoiceChannelId)) {
      voiceMedia.leave();
      return;
    }
    void voiceMedia
      .activateChannel(myVoiceChannelId, meId, audioBitrateKbps)
      .then(() => voiceMedia.syncParticipants(myVoiceChannelId, meId, room))
      .catch((err) => {
        voiceMedia.setError(err instanceof Error ? err.message : 'microphone_access_failed');
      });
  }, [audioBitrateKbps, meId, myVoiceChannelId, room]);

  useEffect(() => {
    void voiceMedia.setAudioBitrateKbps(audioBitrateKbps);
  }, [audioBitrateKbps]);

  useEffect(() => {
    if (!meId || !myVoiceChannelId) return;
    void voiceMedia.syncParticipants(myVoiceChannelId, meId, room).catch((err) => {
      voiceMedia.setError(err instanceof Error ? err.message : 'microphone_access_failed');
    });
  }, [meId, myVoiceChannelId, room]);

  useEffect(() => {
    voiceMedia.setMuted((myVoiceState?.selfMute ?? false) || (myVoiceState?.serverMute ?? false));
    voiceMedia.setDeafened((myVoiceState?.selfDeaf ?? false) || (myVoiceState?.serverDeaf ?? false));
  }, [
    myVoiceState?.selfDeaf,
    myVoiceState?.selfMute,
    myVoiceState?.serverDeaf,
    myVoiceState?.serverMute,
  ]);

  const watchingUserId = useVoiceUiStore((s) => s.watchingUserId);

  // Screen sound follows the stream you OPENED — see the rule in RemoteAudio.
  // There used to be an exception here that let a lone share play unprompted;
  // it made every share start talking at you the moment it began, which is not
  // how a stream should behave. The exception existed because DM calls had no
  // way to say what they were showing; they set `watchingUserId` themselves now.

  return (
    <div className="hidden" aria-hidden="true">
      {snapshot.remoteStreams.map((remote) => (
        <RemoteAudio
          key={`${remote.userId}:${remote.mediaTag}`}
          stream={remote.stream}
          userId={remote.userId}
          channelId={myVoiceChannelId}
          deafened={snapshot.deafened}
          isScreenAudio={remote.mediaTag === 'screen-audio'}
          watchingUserId={watchingUserId}
        />
      ))}
    </div>
  );
}

// One shared output AudioContext for all remote participants (per-element
// contexts would hit the browser's small AudioContext cap in big rooms).
let outputCtx: AudioContext | null = null;

/**
 * Route the shared output context to the user's chosen speaker via
 * `AudioContext.setSinkId` (Chromium/Electron 110+). All remote audio flows
 * through this one context → one call re-routes everyone. Firefox/Safari lack
 * the API, so they stay on the OS default — matching the "informational on
 * non-Chromium" contract. Safe to call any time; no-ops without a context.
 */
async function applyOutputSink(): Promise<void> {
  const ctx = outputCtx;
  if (!ctx) return;
  const withSink = ctx as AudioContext & { setSinkId?: (id: string) => Promise<void> };
  if (typeof withSink.setSinkId !== 'function') return;
  try {
    await withSink.setSinkId(resolveOutputSink(getVoicePreferences().outputDeviceId));
  } catch {
    /* chosen device gone / unsupported — stays on the current/default sink */
  }
}

function getOutputContext(): AudioContext | null {
  try {
    if (!outputCtx || outputCtx.state === 'closed') {
      const Ctor = getAudioContextCtor();
      outputCtx = new Ctor();
      // Apply the saved output device as soon as the context exists.
      void applyOutputSink();
    }
    if (outputCtx.state === 'suspended') void outputCtx.resume().catch(() => {});
    return outputCtx;
  } catch {
    return null;
  }
}

/** Exported for tests: the mute logic here decides whether screen-share audio
 *  is audible at all, which was the «в демонстрации нет звука» regression. */
export function RemoteAudio(props: {
  stream: MediaStream;
  userId: string;
  channelId: string | null;
  deafened: boolean;
  isScreenAudio?: boolean;
  /** Whose stream the viewer currently has OPEN. Only that participant's
   *  screen sound is audible; everyone else's is silent until you open it. */
  watchingUserId?: string | null;
}) {
  const ref = useRef<HTMLAudioElement | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const [outputGain, setOutputGain] = useState(() => getVoicePreferences().outputGain);
  // Optimistically assume the WebAudio path; flip to false only if it fails.
  // Drives the element's `muted` so it NEVER plays directly while WebAudio is
  // active (otherwise you'd hear the element at fixed volume PLUS the gain
  // node — the slider would only move part of the sound).
  const [webAudio, setWebAudio] = useState(true);
  // Re-render when the listener changes this participant's volume / local mute.
  useVoiceLocalAudio();

  // This is the global output slider in Voice settings. It must be part of
  // every remote stream's gain, including a direct call (channelId is null).
  useEffect(
    () => subscribeVoicePreferences(() => setOutputGain(getVoicePreferences().outputGain)),
    [],
  );

  useEffect(() => {
    const audio = ref.current;
    if (!audio) return;
    if (audio.srcObject !== props.stream) {
      audio.srcObject = props.stream;
    }
  }, [props.stream]);

  // Route the STREAM (not the element) through a GainNode so volume can exceed
  // 100% (boost) — `<audio>.volume` caps at 1.0, and `createMediaElementSource`
  // does NOT capture a WebRTC `srcObject` stream in Chrome (the element keeps
  // playing directly, ignoring the gain). `createMediaStreamSource` taps the
  // stream itself; the <audio> element stays muted purely as a keepalive that
  // some Chrome versions need for the stream to flow. Rebuilt when the stream
  // changes. Falls back to `<audio>.volume` if WebAudio setup fails.
  useEffect(() => {
    const ctx = getOutputContext();
    if (!ctx) {
      setWebAudio(false);
      return;
    }
    let src: MediaStreamAudioSourceNode;
    try {
      src = ctx.createMediaStreamSource(props.stream);
    } catch {
      setWebAudio(false); // unsupported — fall back to direct element playback.
      return;
    }
    const gain = ctx.createGain();
    gainRef.current = gain;
    // Soft limiter so boosting past 100% gets loud without harsh clipping.
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -1;
    limiter.knee.value = 0;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.1;
    src.connect(gain).connect(limiter).connect(ctx.destination);
    setWebAudio(true);
    return () => {
      gainRef.current = null;
      for (const node of [src, gain, limiter]) {
        try {
          node.disconnect();
        } catch {
          /* detached */
        }
      }
    };
  }, [props.stream]);

  // Keep the same upper bound as the per-participant/channel gain. The
  // output preference is a final master multiplier, so 200% doubles whatever
  // volume the listener has selected for this participant.
  const gain = Math.min(8, effectiveGain(props.userId, props.channelId) * (outputGain / 100));
  
  // Screen sound is audible only from the stream you OPENED — the same rule
  // Discord has, and the one people expect: a share that starts in the channel
  // does not begin talking at you, it waits until you look at it.
  //
  // Before, a lone share played unprompted. That is fine with one sharer and
  // wrong the moment there are three: each new share is another desktop's
  // sound arriving unasked, and it does not stop being wrong just because
  // there is currently only one of them. Tiles you are not watching carry a
  // «нажмите, чтобы слышать» hint, so the silence is explained rather than
  // read as the broken-screen-audio bug it used to be.
  //
  // Every surface that puts a stream on screen is responsible for saying so
  // (`setWatchingUserId`), including the DM call, which shows a peer's screen
  // without any click.
  const isScreenAudioMuted = props.isScreenAudio && props.watchingUserId !== props.userId;
  const muted = props.deafened || isLocallyMuted(props.userId) || isScreenAudioMuted;

  useEffect(() => {
    if (gainRef.current) {
      // WebAudio path: the GainNode is the only audible output (the element is
      // muted via the JSX prop). Gain carries volume + boost + mute.
      gainRef.current.gain.value = muted ? 0 : gain;
    } else {
      // Fallback: element plays directly, capped at 1 (mute is the JSX prop).
      const audio = ref.current;
      if (audio) audio.volume = Math.min(1, gain);
    }
  });

  // Fallback path only (WebAudio failed → the element plays directly): pin its
  // output to the chosen speaker via HTMLMediaElement.setSinkId, re-applying on
  // change. In the WebAudio path the element is muted and the sink is handled on
  // the shared context, so this is skipped.
  useEffect(() => {
    const el = ref.current;
    if (!el || webAudio) return;
    const apply = () => {
      const withSink = el as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
      if (typeof withSink.setSinkId !== 'function') return;
      void withSink.setSinkId(resolveOutputSink(getVoicePreferences().outputDeviceId)).catch(() => {});
    };
    apply();
    return subscribeVoicePreferences(apply);
  }, [webAudio]);

  // In the WebAudio path the element is always muted (keepalive only) so it
  // can't double-play; in the fallback it reflects the real mute state.
  return <audio ref={ref} autoPlay playsInline muted={webAudio || muted} />;
}
